import {
  cloneElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'

export type TooltipPlacement =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-start'
  | 'top-end'
  | 'bottom-start'
  | 'bottom-end'

export interface TooltipProps {
  /** The tooltip content — text or rich ReactNode. */
  content: ReactNode
  /** Single trigger element. Handlers are injected via cloneElement. */
  children: ReactElement
  /** Preferred placement. Auto-flips on viewport edge. Default: 'top'. */
  placement?: TooltipPlacement
  /** Hover delay in ms. Focus is always immediate. Default: 400. */
  delay?: number
  /** When true the tooltip is never shown. */
  disabled?: boolean
  /** id for aria-describedby. Auto-generated if omitted. */
  id?: string
  /**
   * Makes the tooltip hoverable + scrollable: the bubble receives pointer
   * events and stays open while the pointer is over it (a short close delay
   * bridges the anchor→bubble gap). Use for rich content that can overflow —
   * e.g. a long day-by-day leave breakdown. Default false keeps the classic
   * pointer-events:none behaviour for every other tooltip.
   */
  interactive?: boolean
  /**
   * Caps the scrollable content height (only meaningful with `interactive`).
   * Content taller than this scrolls inside the bubble. Default '60vh'.
   */
  maxHeight?: number | string
}

// ── position maths ────────────────────────────────────────────────────────────

interface Coords {
  top: number
  left: number
  /** Resolved placement after viewport-edge flip. */
  resolvedPlacement: TooltipPlacement
  /** Arrow centre in tooltip-local coords along the cross axis (px from the
   *  tooltip's left edge for top/bottom, from the top edge for left/right).
   *  Computed from the anchor centre so the tip points at the trigger even
   *  for -start/-end placements and after viewport clamping. */
  arrowOffset: number
}

const ARROW_SIZE = 6
const GAP = 8 // gap between anchor and tooltip (incl. arrow)

function computePosition(
  anchorRect: DOMRect,
  tooltipRect: DOMRect,
  preferredPlacement: TooltipPlacement,
): Coords {
  const { innerWidth: vw, innerHeight: vh } = window
  const scrollX = window.scrollX
  const scrollY = window.scrollY

  const aw = anchorRect.width
  const ah = anchorRect.height
  const tw = tooltipRect.width
  const th = tooltipRect.height

  // Determine primary axis from preferred placement
  const primary = preferredPlacement.split('-')[0] as 'top' | 'bottom' | 'left' | 'right'
  const secondary = preferredPlacement.split('-')[1] as 'start' | 'end' | undefined

  // Flip primary axis if there isn't enough room
  let resolvedPrimary = primary
  const spaceTop = anchorRect.top
  const spaceBottom = vh - anchorRect.bottom
  const spaceLeft = anchorRect.left
  const spaceRight = vw - anchorRect.right
  const needed = th + GAP

  if (primary === 'top' && spaceTop < needed && spaceBottom >= needed) resolvedPrimary = 'bottom'
  else if (primary === 'bottom' && spaceBottom < needed && spaceTop >= needed) resolvedPrimary = 'top'
  else if (primary === 'left' && spaceLeft < tw + GAP && spaceRight >= tw + GAP) resolvedPrimary = 'right'
  else if (primary === 'right' && spaceRight < tw + GAP && spaceLeft >= tw + GAP) resolvedPrimary = 'left'

  const resolvedPlacement: TooltipPlacement = secondary
    ? (`${resolvedPrimary}-${secondary}` as TooltipPlacement)
    : resolvedPrimary

  // Compute top/left for the resolved placement
  let top = 0
  let left = 0

  if (resolvedPrimary === 'top') {
    top = anchorRect.top + scrollY - th - GAP
    left = secondary === 'start'
      ? anchorRect.left + scrollX
      : secondary === 'end'
        ? anchorRect.right + scrollX - tw
        : anchorRect.left + scrollX + aw / 2 - tw / 2
  } else if (resolvedPrimary === 'bottom') {
    top = anchorRect.bottom + scrollY + GAP
    left = secondary === 'start'
      ? anchorRect.left + scrollX
      : secondary === 'end'
        ? anchorRect.right + scrollX - tw
        : anchorRect.left + scrollX + aw / 2 - tw / 2
  } else if (resolvedPrimary === 'left') {
    left = anchorRect.left + scrollX - tw - GAP
    top = anchorRect.top + scrollY + ah / 2 - th / 2
  } else {
    // right
    left = anchorRect.right + scrollX + GAP
    top = anchorRect.top + scrollY + ah / 2 - th / 2
  }

  // Clamp to viewport (8px edge margin)
  const margin = 8
  left = Math.max(margin + scrollX, Math.min(left, vw + scrollX - tw - margin))
  top = Math.max(margin + scrollY, Math.min(top, vh + scrollY - th - margin))

  // Point the arrow at the anchor centre, in tooltip-local coords, clamped so
  // the tip stays within the bubble's rounded corners.
  const inset = ARROW_SIZE + 6
  let arrowOffset: number
  if (resolvedPrimary === 'top' || resolvedPrimary === 'bottom') {
    const anchorCenterX = anchorRect.left + scrollX + aw / 2
    arrowOffset = Math.max(inset, Math.min(anchorCenterX - left, tw - inset))
  } else {
    const anchorCenterY = anchorRect.top + scrollY + ah / 2
    arrowOffset = Math.max(inset, Math.min(anchorCenterY - top, th - inset))
  }

  return { top, left, resolvedPlacement, arrowOffset }
}

// ── arrow styles ──────────────────────────────────────────────────────────────

function arrowStyle(placement: TooltipPlacement, arrowOffset: number): CSSProperties {
  const primary = placement.split('-')[0] as 'top' | 'bottom' | 'left' | 'right'
  const base: CSSProperties = {
    position: 'absolute',
    width: 0,
    height: 0,
    pointerEvents: 'none',
  }
  const s = ARROW_SIZE
  if (primary === 'top') {
    return {
      ...base,
      bottom: -s,
      left: arrowOffset,
      transform: 'translateX(-50%)',
      borderLeft: `${s}px solid transparent`,
      borderRight: `${s}px solid transparent`,
      borderTop: `${s}px solid var(--text)`,
    }
  }
  if (primary === 'bottom') {
    return {
      ...base,
      top: -s,
      left: arrowOffset,
      transform: 'translateX(-50%)',
      borderLeft: `${s}px solid transparent`,
      borderRight: `${s}px solid transparent`,
      borderBottom: `${s}px solid var(--text)`,
    }
  }
  if (primary === 'left') {
    return {
      ...base,
      right: -s,
      top: arrowOffset,
      transform: 'translateY(-50%)',
      borderTop: `${s}px solid transparent`,
      borderBottom: `${s}px solid transparent`,
      borderLeft: `${s}px solid var(--text)`,
    }
  }
  // right
  return {
    ...base,
    left: -s,
    top: arrowOffset,
    transform: 'translateY(-50%)',
    borderTop: `${s}px solid transparent`,
    borderBottom: `${s}px solid transparent`,
    borderRight: `${s}px solid var(--text)`,
  }
}

// ── translate offset for entry animation ─────────────────────────────────────

function entryTranslate(placement: TooltipPlacement, entered: boolean): string {
  const primary = placement.split('-')[0] as 'top' | 'bottom' | 'left' | 'right'
  if (entered) return 'translate(0, 0)'
  if (primary === 'top') return 'translate(0, 4px)'
  if (primary === 'bottom') return 'translate(0, -4px)'
  if (primary === 'left') return 'translate(4px, 0)'
  return 'translate(-4px, 0)'
}

// ── reduce-motion helper ──────────────────────────────────────────────────────

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

// ── Tooltip component ─────────────────────────────────────────────────────────

/**
 * Hover/focus tooltip. Renders into a portal so it is never clipped by
 * overflow:hidden parents. Supports 8 placements with auto-flip on viewport
 * edges. Touch-primary devices: tooltip is suppressed via CSS
 * `@media (hover: none)`.
 *
 * Usage:
 *   <Tooltip content="Approved by Ben" placement="top">
 *     <button>...</button>
 *   </Tooltip>
 */
export function Tooltip({
  content,
  children,
  placement = 'top',
  delay = 400,
  disabled = false,
  id,
  interactive = false,
  maxHeight = '60vh',
}: TooltipProps): JSX.Element {
  const autoId = useId()
  const tooltipId = id ?? `tooltip-${autoId}`

  const [visible, setVisible] = useState(false)
  const [entered, setEntered] = useState(false)
  const [coords, setCoords] = useState<Coords | null>(null)

  const anchorRef = useRef<HTMLElement | null>(null)
  const tooltipRef = useRef<HTMLDivElement | null>(null)
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearHoverTimer = useCallback((): void => {
    if (hoverTimer.current !== null) {
      clearTimeout(hoverTimer.current)
      hoverTimer.current = null
    }
  }, [])

  const clearHideTimer = useCallback((): void => {
    if (hideTimer.current !== null) {
      clearTimeout(hideTimer.current)
      hideTimer.current = null
    }
  }, [])

  // Position after the tooltip mounts / on resize
  const reposition = useCallback((): void => {
    if (!anchorRef.current || !tooltipRef.current) return
    const anchorRect = anchorRef.current.getBoundingClientRect()
    const tooltipRect = tooltipRef.current.getBoundingClientRect()
    setCoords(computePosition(anchorRect, tooltipRect, placement))
  }, [placement])

  useEffect(() => {
    if (!visible) return
    // First paint: position after mount (two-frame to let the browser measure)
    const raf = requestAnimationFrame(() => {
      reposition()
      requestAnimationFrame(() => setEntered(true))
    })
    return () => cancelAnimationFrame(raf)
  }, [visible, reposition])

  // Re-position on scroll / resize while open
  useEffect(() => {
    if (!visible) return
    window.addEventListener('scroll', reposition, { passive: true, capture: true })
    window.addEventListener('resize', reposition, { passive: true })
    return () => {
      window.removeEventListener('scroll', reposition, { capture: true })
      window.removeEventListener('resize', reposition)
    }
  }, [visible, reposition])

  const show = useCallback(
    (immediate: boolean): void => {
      if (disabled) return
      clearHoverTimer()
      if (immediate) {
        setVisible(true)
      } else {
        hoverTimer.current = setTimeout(() => setVisible(true), delay)
      }
    },
    [disabled, delay, clearHoverTimer],
  )

  const hideNow = useCallback((): void => {
    clearHoverTimer()
    clearHideTimer()
    setVisible(false)
    setEntered(false)
    setCoords(null)
  }, [clearHoverTimer, clearHideTimer])

  const hide = useCallback((): void => {
    // Interactive tooltips defer the close so the pointer can travel across the
    // anchor→bubble gap without dismissing it; entering the bubble cancels it.
    if (interactive) {
      clearHoverTimer()
      clearHideTimer()
      hideTimer.current = setTimeout(hideNow, 140)
      return
    }
    hideNow()
  }, [interactive, clearHoverTimer, clearHideTimer, hideNow])

  // Clean up any pending close timer on unmount.
  useEffect(() => () => clearHideTimer(), [clearHideTimer])

  // Keyboard: hide on Escape
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent): void => {
      if (e.key === 'Escape') hide()
      // Forward the original handler if present
      const orig = (children.props as Record<string, unknown>).onKeyDown
      if (typeof orig === 'function') {
        ;(orig as (e: React.KeyboardEvent) => void)(e)
      }
    },
    [hide, children.props],
  )

  // Inject event handlers onto the single child trigger
  const triggerProps: Record<string, unknown> = {
    'aria-describedby': visible ? tooltipId : undefined,
    ref: (el: HTMLElement | null) => {
      anchorRef.current = el
      // Forward any existing ref on the child
      const existingRef = (children as { ref?: React.Ref<unknown> }).ref
      if (typeof existingRef === 'function') existingRef(el)
      else if (existingRef && typeof existingRef === 'object') {
        ;(existingRef as React.MutableRefObject<HTMLElement | null>).current = el
      }
    },
    onMouseEnter: () => show(false),
    onMouseLeave: hide,
    onFocus: () => show(true),
    onBlur: hide,
    onKeyDown: handleKeyDown,
  }

  const reduced = prefersReducedMotion()
  const resolved = coords?.resolvedPlacement ?? placement

  const tooltipStyle: CSSProperties = {
    position: 'absolute',
    top: coords?.top ?? 0,
    left: coords?.left ?? 0,
    // Hide (opacity 0, pointer-events none) until positioned to avoid flash.
    opacity: coords && entered ? 1 : 0,
    // Interactive tooltips must receive pointer events so the user can move into
    // the bubble and scroll it; classic tooltips stay click-through.
    pointerEvents: interactive ? 'auto' : 'none',
    transform: reduced ? 'none' : entryTranslate(resolved, !!(coords && entered)),
    transition: reduced
      ? 'none'
      : `opacity var(--motion-duration-base) var(--motion-ease-out),
         transform var(--motion-duration-base) var(--motion-ease-out)`,
    zIndex: 1000,
    // Touch-primary: hide entirely via media query (declared inline as a
    // workaround — callers can also add a CSS class to achieve this)
    // Note: inline styles cannot use @media; we suppress visibility via JS
    // matchMedia in addition to the CSS layer.
  }

  const boxStyle: CSSProperties = {
    position: 'relative',
    background: 'var(--text)',
    color: 'var(--surface)',
    borderRadius: 6,
    padding: '8px 10px',
    fontSize: 12,
    lineHeight: 1.4,
    maxWidth: 240,
    boxShadow: '0 4px 12px rgba(80, 56, 28, 0.18)',
    fontFamily: 'var(--font-sans)',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  }

  // Suppress on touch-primary devices (matchMedia check at show-time is in
  // `show`, but also guard portal mount entirely)
  const isTouchPrimary =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(hover: none)').matches

  return (
    <>
      {cloneElement(children, triggerProps)}
      {visible && !isTouchPrimary
        ? createPortal(
            <div
              ref={tooltipRef}
              role="tooltip"
              id={tooltipId}
              data-testid="tooltip"
              data-placement={resolved}
              style={tooltipStyle}
              {...(interactive
                ? {
                    onMouseEnter: () => {
                      clearHoverTimer()
                      clearHideTimer()
                    },
                    onMouseLeave: hide,
                  }
                : {})}
            >
              <div style={boxStyle}>
                {/* Inner scroll region so tall content (a long day-by-day
                    breakdown) scrolls inside the bubble instead of overflowing
                    the viewport. The arrow stays on the outer box so it isn't
                    clipped by the scroll container. */}
                <div
                  style={
                    interactive
                      ? { maxHeight, overflowY: 'auto', overflowX: 'hidden' }
                      : undefined
                  }
                >
                  {content}
                </div>
                <div aria-hidden="true" style={arrowStyle(resolved, coords?.arrowOffset ?? ARROW_SIZE + 6)} />
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
